/* ──────────────────────────────────────────────────────────────────────────
   گزارش کلی حسابداری — از دکمهٔ «گزارش کلی» صفحهٔ سند حسابداری، در یک تب کامل.

   چیدمان:
     بالا    نوع گزارش، سال، بازهٔ تاریخ و شمارهٔ سند، تیک‌های فوری و دکمه‌ها
     وسط    «شرایط گزارش»: ده گزینهٔ پرکاربرد + «سایر گزینه‌ها» به تفکیک دسته
            هر گزینه: نقش (— / سطر / سرگروه / زیرگروه) + فیلتر (انتخاب / عدم انتخاب)
     نتیجه   در پنجرهٔ بزرگ جدا: جدول ساده (هر ردیف یک خط)، با امکان
             نمایش سرگروه‌ها و جمع‌ها؛ کلیک‌راست روی هر مبلغ ⇐ ریز اقلام
     چاپ     پیش‌نمایش در پنجرهٔ بزرگ، بدون باز شدن صفحهٔ جدید مرورگر

   همهٔ محاسبه‌ها در سرور است؛ این صفحه فقط انتخاب‌ها را می‌فرستد و نتیجه را نشان می‌دهد.
   ────────────────────────────────────────────────────────────────────────── */
import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import ListPage, { ui } from '../../components/ListPage';
import { MoreButton, PAGE_SIZE } from '../../components/paging';
import JalaliDatePicker from '../../components/JalaliDatePicker';
import WarningDialog from '../../components/WarningDialog';
import { exportGrid } from '../../components/excel';
import useColumnResize from '../../hooks/useColumnResize';
import DocumentDetailModal from './DocumentDetailModal';
import { jsonOf, netErr } from '../../utils/http';

const toFa = n => (n === null || n === undefined ? '' : String(n).replace(/[0-9]/g, d => '۰۱۲۳۴۵۶۷۸۹'[d]));
const toEng = s => String(s ?? '').replace(/[۰-۹]/g, d => '۰۱۲۳۴۵۶۷۸۹'.indexOf(d).toString());
const money = n => {
  const v = Number(n || 0);
  if (!v) return '۰';
  return v.toLocaleString('fa-IR');
};
const auth = () => ({ Authorization: `Bearer ${localStorage.getItem('access')}` });
const API = (path, opts = {}) => fetch(`/api/accounting/general-report${path}`, {
  ...opts,
  headers: { 'Content-Type': 'application/json', ...auth(), ...(opts.headers || {}) },
}).then(jsonOf, netErr);

const ROLES = [
  { v: '', label: '—', tip: 'در گزارش نمی‌آید' },
  { v: 'row', label: 'سطر', tip: 'هر مقدار این گزینه یک سطر گزارش است' },
  { v: 'group', label: 'سرگروه', tip: 'سطرها زیر عنوان این گزینه گروه‌بندی می‌شوند' },
  { v: 'sub', label: 'زیرگروه', tip: 'گروه میانی با جمع، بین سرگروه و سطرها' },
];
const ROLE_COLORS = { row: '#1565c0', group: '#6a1b9a', sub: '#00796b' };
const JAZB = 11;
const GUARANTEES = 12;
const PIVOT = 13;

/* ═════════════════════════ کلید نقش ═════════════════════════ */
function RoleSwitch({ value, onChange, disabled, pivot }) {
  return (
    <div style={s.seg}>
      {ROLES.filter(r => !(pivot && r.v === 'sub')).map(r => {
        // در گزارش انتخابی سرگروه و زیرگروه هر دو ستون می‌شوند، پس یک دکمهٔ «ستون» کافی است
        const on = (value || '') === r.v || (pivot && r.v === 'group' && value === 'sub');
        const label = pivot && r.v === 'group' ? 'ستون' : r.label;
        return (
          <button key={r.v || 'none'} type="button" disabled={disabled}
            title={pivot && r.v && r.v !== 'row' ? 'در گزارش انتخابی، مقدارهای این گزینه ستون‌های گزارش می‌شوند' : r.tip}
            onClick={() => onChange(r.v)}
            style={{
              ...s.segBtn,
              ...(on ? { background: r.v ? ROLE_COLORS[r.v] : '#90a4ae', color: '#fff', fontWeight: 700 } : {}),
              ...(disabled ? { opacity: 0.35, cursor: 'not-allowed' } : {}),
            }}>
            {label}
          </button>
        );
      })}
    </div>
  );
}

/* ═════════════════════════ یک گزینه ═════════════════════════ */
function filterLabel(f) {
  if (!f || !f.items) return { text: 'همه', active: false };
  const n = f.items.length;
  let sel = 0, desel = 0;
  f.items.forEach(it => {
    const m = f.marks[String(it.id)] || 'sel';
    if (m === 'sel') sel += 1; else if (m === 'desel') desel += 1;
  });
  if (desel) return { text: `به‌جز ${toFa(desel)}`, active: true };
  if (sel === n) return { text: 'همه', active: false };
  return { text: `${toFa(sel)} از ${toFa(n)}`, active: true };
}

function DimRow({ dim, role, filter, pivot, onRole, onFilter }) {
  const fl = filterLabel(filter);
  return (
    <div style={{ ...s.dimRow, ...(role ? { borderColor: ROLE_COLORS[role], background: '#fbfcff' } : {}) }}>
      <div style={s.dimTitle} title={dim.title}>
        {role && <span style={{ ...s.dot, background: ROLE_COLORS[role] }} />}
        {dim.title}
      </div>
      <RoleSwitch value={role} onChange={v => onRole(dim.key, v)} disabled={dim.filter_only} pivot={pivot} />
      <button type="button" onClick={() => onFilter(dim)}
        style={{ ...s.filterBtn, ...(fl.active ? s.filterBtnOn : {}) }}
        title="انتخاب / عدم انتخاب موارد">
        ⚲ {fl.text}
      </button>
    </div>
  );
}

/* ═════════════════════════ پنجرهٔ انتخاب موارد ═════════════════════════ */
function FilterModal({ dim, filter, loading, onChange, onClose, allSub, onAllSub }) {
  const [q, setQ] = useState('');
  const [warn, setWarn] = useState('');
  const searchRef = useRef(null);
  const items = useMemo(() => (filter && filter.items) || [], [filter]);
  const marks = (filter && filter.marks) || {};
  const shown = items.filter(it => !q || String(it.title || '').includes(q) || String(it.code || '').includes(q));
  const markOf = it => marks[String(it.id)] || 'sel';
  const counted = items.filter(it => markOf(it) !== 'unsel').length;

  const setMarks = fn => {
    const next = { ...marks };
    shown.forEach(it => { next[String(it.id)] = fn(markOf(it)); });
    setWarn('');
    onChange(next);
  };
  const toggle = (it, col) => {
    const cur = markOf(it);
    const next = col === 'desel' ? (cur === 'desel' ? 'unsel' : 'desel') : (cur === 'sel' ? 'unsel' : 'sel');
    setWarn('');
    onChange({ ...marks, [String(it.id)]: next });
  };
  const close = () => {
    if (items.length && counted === 0) { setWarn(`حداقل یکی از موارد «${dim.title}» باید انتخاب شود`); return; }
    onClose();
  };
  useEffect(() => { searchRef.current?.focus(); }, []);
  const onKey = e => {
    if (e.key === '+') { e.preventDefault(); setMarks(() => 'sel'); }
    else if (e.key === '-') { e.preventDefault(); setMarks(() => 'unsel'); }
    else if (e.key === '*') { e.preventDefault(); setMarks(m => (m === 'sel' ? 'unsel' : 'sel')); }
    else if (e.key === 'Escape') { e.preventDefault(); close(); }
  };

  return (
    <div style={ui.overlay} onMouseDown={e => { if (e.target === e.currentTarget) close(); }}>
      <div style={{ ...ui.modal, maxWidth: '720px', height: '82vh' }}>
        <div style={ui.mHeader}>
          <span style={ui.mTitle}>⚲ {dim.title}</span>
          <button style={ui.closeBtn} onClick={close}>✕</button>
        </div>
        <div style={{ ...ui.mBody, display: 'flex', flexDirection: 'column', gap: '10px', padding: '14px 18px' }}>
          <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
            <input ref={searchRef} style={{ ...ui.input, flex: 1 }} value={q} onKeyDown={onKey}
              onChange={e => setQ(e.target.value.replace(/[+*-]$/, ''))} placeholder="جستجو در کد یا عنوان…" />
            <button style={s.smallBtn} onClick={() => setMarks(() => 'sel')} title="کلید +">همه</button>
            <button style={s.smallBtn} onClick={() => setMarks(() => 'unsel')} title="کلید -">هیچ</button>
            <button style={s.smallBtn} onClick={() => setMarks(m => (m === 'sel' ? 'unsel' : 'sel'))} title="کلید *">معکوس</button>
          </div>
          {dim.key === 'AccountingSubCodingCode' && (
            <label style={s.chk}>
              <input type="checkbox" checked={!!allSub} onChange={e => onAllSub(e.target.checked)} />
              فقط حسابهای دارای عملکرد
            </label>
          )}
          {warn && <div style={s.warnLine}>⚠ {warn}</div>}
          <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '12px', color: '#555' }}>
            <span>انتخاب شده: <b>{toFa(counted)}</b> از {toFa(items.length)}</span>
            <span style={{ color: '#888' }}>«عدم انتخاب» یعنی این موارد از گزارش کنار گذاشته شوند</span>
          </div>
          <div style={{ flex: 1, overflow: 'auto', border: '1px solid #e3e7ee', borderRadius: '10px' }}>
            <table style={{ ...ui.table, tableLayout: 'fixed' }}>
              <thead>
                <tr>
                  <th style={{ ...s.thSticky, width: '120px' }}>کد</th>
                  <th style={{ ...s.thSticky }}>عنوان</th>
                  <th style={{ ...s.thSticky, width: '72px' }}>انتخاب</th>
                  <th style={{ ...s.thSticky, width: '84px' }}>عدم انتخاب</th>
                </tr>
              </thead>
              <tbody>
                {loading && <tr><td colSpan={4} style={ui.empty}>⏳ در حال بارگذاری…</td></tr>}
                {!loading && shown.length === 0 && <tr><td colSpan={4} style={ui.empty}>موردی یافت نشد</td></tr>}
                {!loading && shown.map((it, i) => {
                  const m = markOf(it);
                  return (
                    <tr key={String(it.id)} style={{ background: m === 'desel' ? '#fff3f0' : (i % 2 ? '#f8f9fc' : '#fff') }}>
                      <td style={{ ...ui.td, color: '#1a2f50', fontWeight: 600 }}>{toFa(it.code)}</td>
                      <td style={{ ...ui.td, textAlign: 'right', whiteSpace: 'normal' }}>{toFa(it.title)}</td>
                      <td style={ui.td}>
                        <input type="checkbox" checked={m === 'sel'} onChange={() => toggle(it, 'sel')} />
                      </td>
                      <td style={ui.td}>
                        <input type="checkbox" checked={m === 'desel'} onChange={() => toggle(it, 'desel')}
                          style={{ accentColor: '#c62828' }} />
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>
        <div style={ui.mFooter}><button style={ui.btnSave} onClick={close}>تایید</button></div>
      </div>
    </div>
  );
}

/* ═════════════════════════ گزارش‌های ذخیره‌شده ═════════════════════════ */
function SavedModal({ list, loading, current, onOpen, onSave, onDelete, onClose }) {
  const [q, setQ] = useState('');
  const [sel, setSel] = useState(current?.id || 0);
  const [title, setTitle] = useState(current?.title || '');
  const [remark, setRemark] = useState(current?.remark || '');
  const [ask, setAsk] = useState(false);
  const shown = list.filter(r => !q || r.title.includes(q) || r.remark.includes(q));
  const pick = r => { setSel(r.id); setTitle(r.title); setRemark(r.remark); };
  return (
    <div style={ui.overlay} onMouseDown={e => { if (e.target === e.currentTarget) onClose(); }}>
      <div style={{ ...ui.modal, maxWidth: '760px', height: '80vh' }}>
        <div style={ui.mHeader}>
          <span style={ui.mTitle}>📁 گزارشات ذخیره شده</span>
          <button style={ui.closeBtn} onClick={onClose}>✕</button>
        </div>
        <div style={{ ...ui.mBody, display: 'flex', flexDirection: 'column', gap: '10px', padding: '14px 18px' }}>
          <input style={ui.input} value={q} onChange={e => setQ(e.target.value)} placeholder="جستجو…" />
          <div style={{ flex: 1, overflow: 'auto', border: '1px solid #e3e7ee', borderRadius: '10px' }}>
            <table style={ui.table}>
              <thead><tr>
                <th style={s.thSticky}>عنوان گزارش</th>
                <th style={s.thSticky}>شرح گزارش</th>
              </tr></thead>
              <tbody>
                {loading && <tr><td colSpan={2} style={ui.empty}>⏳ در حال بارگذاری…</td></tr>}
                {!loading && shown.length === 0 && <tr><td colSpan={2} style={ui.empty}>هیچ عنوانی تعریف نشده است.</td></tr>}
                {!loading && shown.map((r, i) => (
                  <tr key={r.id} onClick={() => pick(r)} onDoubleClick={() => onOpen(r.id)}
                    style={{ cursor: 'pointer', background: sel === r.id ? '#e3f2fd' : (i % 2 ? '#f8f9fc' : '#fff') }}>
                    <td style={{ ...ui.td, textAlign: 'right', fontWeight: 600 }}>{toFa(r.title)}</td>
                    <td style={{ ...ui.td, textAlign: 'right', color: '#555', whiteSpace: 'normal' }}>{toFa(r.remark)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '10px' }}>
            <div>
              <div style={ui.mlabel}>عنوان گزارش</div>
              <input style={ui.input} value={title} onChange={e => setTitle(e.target.value)} />
            </div>
            <div>
              <div style={ui.mlabel}>شرح گزارش</div>
              <input style={ui.input} value={remark} onChange={e => setRemark(e.target.value)} />
            </div>
          </div>
        </div>
        <div style={{ ...ui.mFooter, justifyContent: 'space-between' }}>
          <div style={{ display: 'flex', gap: '8px' }}>
            <button style={{ ...ui.btnDel, cursor: sel ? 'pointer' : 'default', opacity: sel ? 1 : 0.4 }}
              disabled={!sel} onClick={() => setAsk(true)}>حذف گزارش</button>
          </div>
          <div style={{ display: 'flex', gap: '8px' }}>
            <button style={{ ...ui.btnEdit, cursor: sel ? 'pointer' : 'default', opacity: sel ? 1 : 0.4 }}
              disabled={!sel} onClick={() => onSave(sel, title, remark)}>ذخیره تغییرات در گزارش فعلی</button>
            <button style={ui.btnNew} onClick={() => onSave(0, title, remark)}>ذخیره بعنوان گزارش جدید</button>
            <button style={{ ...ui.btnItems, cursor: sel ? 'pointer' : 'default', opacity: sel ? 1 : 0.4 }}
              disabled={!sel} onClick={() => onOpen(sel)}>باز کردن</button>
          </div>
        </div>
      </div>
      {ask && (
        <WarningDialog title="هشدار" tone="danger" lines={['آیا از حذف این گزارش مطمئن هستید؟']}
          confirmLabel="بلی" cancelLabel="خیر"
          onCancel={() => setAsk(false)} onConfirm={() => { setAsk(false); onDelete(sel); setSel(0); }} />
      )}
    </div>
  );
}

/* ═════════════════════════ چاپ ═════════════════════════ */
function PrintModal({ report, opts, setOpts, busy, onPrint, onClose }) {
  const set = (k, v) => setOpts(o => ({ ...o, [k]: v }));
  return (
    <div style={ui.overlay} onMouseDown={e => { if (e.target === e.currentTarget) onClose(); }}>
      <div style={{ ...ui.modal, maxWidth: '520px' }}>
        <div style={ui.mHeader}>
          <span style={ui.mTitle}>🖨️ چاپ گزارش</span>
          <button style={ui.closeBtn} onClick={onClose}>✕</button>
        </div>
        <div style={{ ...ui.mBody, display: 'flex', flexDirection: 'column', gap: '12px' }}>
          <div>
            <div style={ui.mlabel}>عنوان گزارش روی چاپ</div>
            <textarea style={{ ...ui.input, minHeight: '64px', resize: 'vertical' }} value={opts.title}
              onChange={e => set('title', e.target.value)} placeholder="خالی بماند: نام نوع گزارش" />
            <div style={{ fontSize: '11px', color: '#888', marginTop: '3px' }}>برای رفتن به خط بعد از «$» استفاده کنید</div>
          </div>
          {report !== JAZB && (
            <label style={s.chk}>
              <input type="checkbox" checked={!!opts.show_date} onChange={e => set('show_date', e.target.checked)} />
              چاپ محدوده تاریخی
            </label>
          )}
          {report === JAZB && (
            <>
              <label style={s.chk}>
                <input type="checkbox" checked={!!opts.show_detail} onChange={e => set('show_detail', e.target.checked)} />
                تفکیک نقد و اسناد
              </label>
              <label style={s.chk}>
                <input type="checkbox" checked={!!opts.show_jazb_percent} onChange={e => set('show_jazb_percent', e.target.checked)} />
                چاپ درصدجذب
              </label>
            </>
          )}
        </div>
        <div style={ui.mFooter}>
          <button style={ui.btnCancel} onClick={onClose}>بستن</button>
          <button style={{ ...ui.btnEdit, cursor: 'pointer' }} disabled={busy} onClick={onPrint}>
            {busy ? '⏳ در حال تهیه…' : '🖨️ نمایش و چاپ'}
          </button>
        </div>
      </div>
    </div>
  );
}

/* ═════════════════════════ ریز اقلام ═════════════════════════ */
const DRILL_COLS = [
  { k: 'سند', w: 60 }, { k: 'تاریخ', w: 88 }, { k: 'حواله', w: 70 }, { k: 'ردیف', w: 50 }, { k: 'حساب', w: 70 },
  { k: 'شرح سند', w: 230, text: true }, { k: 'شرح ریز', w: 230, text: true },
  { k: 'بدهکار', w: 120, num: true }, { k: 'بستانکار', w: 120, num: true },
  { k: 'شماره دریافت/پرداخت', w: 130 }, { k: 'مبلغ دریافت/پرداخت', w: 120, num: true }, { k: 'مغایرت', w: 110, num: true },
];

function DrillPane({ drill, onClose, onOpenDoc }) {
  const [q, setQ] = useState('');
  const hasInOut = drill.rows.some(r => Number(r['مبلغ دریافت/پرداخت'] || 0) !== 0);
  const cols = DRILL_COLS.filter(c => hasInOut || !['مبلغ دریافت/پرداخت', 'مغایرت', 'شماره دریافت/پرداخت'].includes(c.k));
  const { widths, TH } = useColumnResize(cols.map(c => c.w));
  const rows = useMemo(() => {
    const t = toEng(q.replace(/,/g, '').trim());
    if (!t || t === '0') return drill.rows;
    if (/^\d+$/.test(t)) return drill.rows.filter(r => Number(r['بدهکار'] || 0) + Number(r['بستانکار'] || 0) === Number(t));
    return drill.rows.filter(r => String(r['شرح سند'] || '').includes(q) || String(r['شرح ریز'] || '').includes(q));
  }, [q, drill.rows]);
  const sd = rows.reduce((a, r) => a + Number(r['بدهکار'] || 0), 0);
  const sc = rows.reduce((a, r) => a + Number(r['بستانکار'] || 0), 0);
  return (
    <div style={s.drill}>
      <div style={s.drillHead}>
        <div style={{ fontWeight: 700, color: '#1a2f50' }}>🔎 ریز اقلام «{drill.column}»</div>
        <input style={{ ...ui.input, width: '240px', padding: '6px 10px' }} value={q}
          onChange={e => setQ(e.target.value)} placeholder="جستجو: مبلغ یا شرح" />
        <div style={s.sumBox}>
          <span>تعداد: <b>{toFa(rows.length)}</b></span>
          <span>جمع بدهکار: <b>{money(sd)}</b></span>
          <span>جمع بستانکار: <b>{money(sc)}</b></span>
          <span>مانده بدهکار: <b>{money(Math.max(sd - sc, 0))}</b></span>
          <span>مانده بستانکار: <b>{money(Math.max(sc - sd, 0))}</b></span>
        </div>
        <button style={{ ...s.smallBtn, marginRight: 'auto' }} onClick={onClose}>✕ بستن</button>
      </div>
      <div style={{ flex: 1, minHeight: 0, overflow: 'auto' }}>
        <table style={{ ...ui.table, tableLayout: 'fixed', width: widths.reduce((a, b) => a + b, 0) }}>
          <thead style={{ position: 'sticky', top: 0, zIndex: 2 }}>
            <tr>{cols.map((c, i) => <TH key={c.k} idx={i} green>{c.k}</TH>)}</tr>
          </thead>
          <tbody>
            {drill.loading && <tr><td colSpan={cols.length} style={ui.empty}>⏳ در حال بارگذاری…</td></tr>}
            {!drill.loading && rows.length === 0 && <tr><td colSpan={cols.length} style={ui.empty}>قلمی یافت نشد</td></tr>}
            {!drill.loading && rows.map((r, i) => (
              <tr key={i} onDoubleClick={() => onOpenDoc(r)} title="دابل‌کلیک: اقلام سند"
                style={{ cursor: 'pointer', background: i % 2 ? '#f3f8f5' : '#fff' }}>
                {cols.map(c => (
                  <td key={c.k} style={{
                    ...ui.td, ...(c.text ? { textAlign: 'right', whiteSpace: 'normal' } : {}),
                    ...(c.num ? { direction: 'ltr', textAlign: 'left', fontWeight: 600 } : {}),
                    ...(c.k === 'مغایرت' && Number(r[c.k] || 0) !== 0 ? { color: '#c62828' } : {}),
                  }}>
                    {c.num ? money(r[c.k]) : toFa(r[c.k])}
                  </td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

/* ═════════════════════════ جدول نتیجه ═════════════════════════ */
function buildDisplay(result, limit, grouped) {
  const cols = result.columns;
  const idx = n => cols.findIndex(c => c.name === n);
  const repI = idx('RepTitle');
  const runI = idx('RunningTotalTitle');
  const vis = cols.map((c, i) => ({ ...c, i })).filter(c => c.visible);
  const numVis = vis.filter(c => c.num);
  const rows = result.rows;
  const hasRep = grouped && repI >= 0 && rows.some(r => String(r[repI] || '').trim());
  const hasRun = grouped && runI >= 0 && rows.some(r => String(r[runI] || '').trim());
  const sumOf = rs => numVis.reduce((o, c) => { o[c.i] = rs.reduce((a, r) => a + Number(r[c.i] || 0), 0); return o; }, {});
  const items = [];
  let gKey = null, sKey = null, gRows = [], sRows = [], gTitle = '', sTitle = '';
  const closeSub = () => { if (hasRun && sRows.length) items.push({ t: 'st', title: sTitle, sums: sumOf(sRows) }); sRows = []; };
  const closeGroup = () => { closeSub(); if (hasRep && gRows.length) items.push({ t: 'gt', title: gTitle, sums: sumOf(gRows) }); gRows = []; };
  const n = Math.min(limit, rows.length);
  for (let k = 0; k < n; k += 1) {
    const r = rows[k];
    const g = hasRep ? String(r[repI] || '') : '';
    const sb = hasRun ? String(r[runI] || '') : '';
    if (g !== gKey) { if (gKey !== null) closeGroup(); gKey = g; sKey = null; gTitle = g; if (hasRep) items.push({ t: 'g', title: g }); }
    if (sb !== sKey) { if (sKey !== null) closeSub(); sKey = sb; sTitle = sb; if (hasRun) items.push({ t: 's', title: sb }); }
    gRows.push(r); sRows.push(r);
    items.push({ t: 'r', r, k });
  }
  if (n > 0 && n === rows.length) closeGroup();
  return { vis, items, grand: sumOf(rows) };
}

function ResultTable({ result, limit, grouped, selected, onSelect, onContext }) {
  const { vis, items, grand } = useMemo(() => buildDisplay(result, limit, grouped), [result, limit, grouped]);
  const init = useMemo(() => vis.map(c => (c.num ? 130 : Math.min(260, Math.max(90, c.name.length * 11)))), [vis]);
  const { widths, setWidths, TH } = useColumnResize(init);
  useEffect(() => { setWidths(init); }, [init, setWidths]);
  const w = i => widths[i] || init[i] || 120;
  const total = vis.reduce((a, _c, i) => a + w(i), 46);
  const span = vis.length + 1;
  const label = t => toFa(String(t || '').split('\n').join(' — '));
  return (
    <table style={{ ...ui.table, tableLayout: 'fixed', width: total, minWidth: '100%' }}>
      <thead style={{ position: 'sticky', top: 0, zIndex: 3 }}>
        <tr>
          <TH style={{ width: 46 }}>ردیف</TH>
          {vis.map((c, i) => <TH key={c.i} idx={i} gold={c.num}>{c.name}</TH>)}
        </tr>
      </thead>
      <tbody>
        {items.map((it, j) => {
          if (it.t === 'g') return <tr key={`g${j}`}><td colSpan={span} style={s.gHead}>{toFa(it.title)}</td></tr>;
          if (it.t === 's') return <tr key={`s${j}`}><td colSpan={span} style={s.sHead}>{toFa(it.title)}</td></tr>;
          if (it.t === 'st' || it.t === 'gt') {
            const st = it.t === 'st' ? s.sTotal : s.gTotal;
            return (
              <tr key={`t${j}`}>
                <td style={st} />
                {vis.map((c, i) => (
                  <td key={c.i} style={{ ...st, ...(c.num ? s.num : { textAlign: 'right' }) }}>
                    {c.num ? money(it.sums[c.i]) : (i === 0 ? `جمع ${label(it.title)}` : '')}
                  </td>
                ))}
              </tr>
            );
          }
          const sel = selected === it.k;
          return (
            <tr key={`r${it.k}`} onClick={() => onSelect(it.k)}
              style={{ background: sel ? '#e3f2fd' : (it.k % 2 ? '#f8f9fc' : '#fff') }}>
              <td style={{ ...ui.td, color: '#888' }}>{toFa(it.k + 1)}</td>
              {vis.map(c => {
                const v = it.r[c.i];
                const neg = c.num && Number(v) < 0;
                return (
                  <td key={c.i}
                    onContextMenu={c.num ? (e => { e.preventDefault(); onSelect(it.k); onContext(it.k, c.name); }) : undefined}
                    style={{ ...ui.td, ...(c.num ? { ...s.num, ...(neg ? { color: '#c62828' } : {}) }
                      : { textAlign: 'right', whiteSpace: 'pre-line' }) }}>
                    {c.num ? money(v) : toFa(v)}
                  </td>
                );
              })}
            </tr>
          );
        })}
        {items.length === 0 && <tr><td colSpan={span} style={ui.empty}>اطلاعاتی برای نمایش وجود ندارد</td></tr>}
      </tbody>
      {items.length > 0 && (
        <tfoot style={{ position: 'sticky', bottom: 0, zIndex: 2 }}>
          <tr>
            <td style={s.foot}>جمع</td>
            {vis.map((c, i) => (
              <td key={c.i} style={{ ...s.foot, ...(c.num ? s.num : {}) }}>
                {c.num ? money(grand[c.i]) : (i === 0 ? `${toFa(result.rows.length)} ردیف` : '')}
              </td>
            ))}
          </tr>
        </tfoot>
      )}
    </table>
  );
}

/* ═════════════════════════ پنجرهٔ نتیجه ═════════════════════════ */
function ResultModal({ boot, st, result, limit, onMore, grouped, setGrouped, selected, onSelect,
                       drill, onDrill, onCloseDrill, onOpenDoc, error, onPrint, onExcel, onSamad, onClose }) {
  const pager = { total: result.rows.length, hasMore: limit < result.rows.length, page: Math.ceil(limit / PAGE_SIZE) };
  const hasGroups = Object.values(st.dims || {}).some(r => r === 'group' || r === 'sub') && st.report !== PIVOT;
  return (
    <div style={{ ...ui.overlay, padding: '10px' }}>
      <div style={s.bigModal}>
        <div style={ui.mHeader}>
          <div style={{ display: 'flex', flexDirection: 'column', gap: '3px', minWidth: 0 }}>
            <span style={ui.mTitle}>📊 گزارش کلی حسابداری — {boot.report_types[st.report]}</span>
            <span style={s.mSub}>
              از تاریخ {toFa(st.from_date)} تا {toFa(st.to_date)} · مبالغ به {boot.scales[st.scale]} · {toFa(result.count)} ردیف
            </span>
          </div>
          <div style={s.mTools}>
            {hasGroups && (
              <label style={{ ...s.chipDark, ...(grouped ? s.chipDarkOn : {}) }}>
                <input type="checkbox" checked={grouped} onChange={e => setGrouped(e.target.checked)} />
                نمایش سرگروه‌ها و جمع‌ها
              </label>
            )}
            <button style={s.btnPrint} onClick={onPrint}>🖨️ گزارش</button>
            <button style={s.btnExcel} onClick={onExcel}>📊 اکسل</button>
            {result.samad && <button style={s.btnSamad} onClick={onSamad}>📁 ایجاد فایل سماد</button>}
            <button style={ui.closeBtn} onClick={onClose} title="بستن">✕</button>
          </div>
        </div>
        {error && <div style={{ ...s.warnLine, margin: '8px 14px 0' }}>⚠ {error}</div>}
        <div style={{ flex: 1, minHeight: 0, display: 'flex', flexDirection: 'column' }}>
          <div style={drill ? ui.topPane : { flex: 1, minHeight: 0, overflow: 'auto' }}>
            <ResultTable result={result} limit={limit} grouped={grouped}
              selected={selected} onSelect={onSelect} onContext={onDrill} />
          </div>
          {drill && (
            <div style={ui.bottomPane}>
              <DrillPane drill={drill} onClose={onCloseDrill} onOpenDoc={onOpenDoc} />
            </div>
          )}
        </div>
        {!drill && (
          <div style={s.mFoot}>
            <span style={{ fontSize: '11.5px', color: '#888' }}>کلیک‌راست روی هر مبلغ: اقلام سازندهٔ آن</span>
            <MoreButton pager={pager} loading={false} onMore={onMore} unit="ردیف" />
            <span />
          </div>
        )}
      </div>
    </div>
  );
}

/* ═════════════════════════ پیش‌نمایش چاپ ═════════════════════════ */
function PrintPreview({ url, title, onClose }) {
  const ref = useRef(null);
  // نوار ابزار خود صفحهٔ چاپی پنهان می‌شود؛ دکمهٔ چاپ در سرِ همین پنجره است
  const onLoad = () => {
    try {
      const d = ref.current.contentDocument;
      const st = d.createElement('style');
      st.textContent = '.toolbar{display:none!important}';
      d.head.appendChild(st);
    } catch (e) { /* اگر دسترسی نبود نوار داخلی می‌ماند */ }
  };
  const doPrint = () => {
    try { ref.current.contentWindow.focus(); ref.current.contentWindow.print(); } catch (e) { /* ignore */ }
  };
  return (
    <div style={{ ...ui.overlay, padding: '10px' }}>
      <div style={{ ...s.bigModal, maxWidth: '1280px' }}>
        <div style={ui.mHeader}>
          <span style={ui.mTitle}>🖨️ {title}</span>
          <div style={s.mTools}>
            <button style={s.btnPrint} onClick={doPrint}>🖨️ چاپ / ذخیره PDF</button>
            <button style={ui.closeBtn} onClick={onClose} title="بستن">✕</button>
          </div>
        </div>
        <iframe ref={ref} src={url} title="پیش‌نمایش چاپ" onLoad={onLoad}
          style={{ flex: 1, width: '100%', border: 'none', background: '#6b7280', borderRadius: '0 0 16px 16px' }} />
      </div>
    </div>
  );
}

/* ═════════════════════════ صفحه ═════════════════════════ */
export default function GeneralReport({ user, onBack, year: initYear }) {
  const [boot, setBoot] = useState(null);                  // init سرور
  const [year, setYear] = useState(String(initYear || ''));
  const yearRef = useRef(String(initYear || ''));
  const [report, setReport] = useState(8);
  const [fromDate, setFromDate] = useState('');
  const [toDate, setToDate] = useState('');
  const [fromNo, setFromNo] = useState('');
  const [toNo, setToNo] = useState('');
  const [interval, setOnlyRange] = useState(false);
  const [kinds, setKinds] = useState({ normal: true, opening: true, finishing: true });
  const [scale, setScale] = useState(0);
  const [value, setValue] = useState(2);
  const [docDetail, setDocDetail] = useState(false);
  const [roles, setRoles] = useState({});
  const [filters, setFilters] = useState({});              // key ⇒ {items, marks, loading}
  const [allSub, setAllSub] = useState(false);
  const [showMore, setShowMore] = useState(false);
  const [dimQ, setDimQ] = useState('');
  const [picking, setPicking] = useState(null);
  const [result, setResult] = useState(null);
  const [limit, setLimit] = useState(PAGE_SIZE);
  const [selected, setSelected] = useState(null);
  const [running, setRunning] = useState(false);
  const [error, setError] = useState('');
  const [toast, setToast] = useState('');
  const [drill, setDrill] = useState(null);
  const [docOpen, setDocOpen] = useState(null);
  const [savedOpen, setSavedOpen] = useState(false);
  const [savedList, setSavedList] = useState([]);
  const [savedLoading, setSavedLoading] = useState(false);
  const [current, setCurrent] = useState(null);            // گزارش ذخیره‌شدهٔ باز
  const [printOpen, setPrintOpen] = useState(false);
  const [printOpts, setPrintOpts] = useState({ title: '', show_date: false, show_detail: false, show_jazb_percent: false });
  const [printing, setPrinting] = useState(false);
  const [lastState, setLastState] = useState(null);       // state همان گزارشی که روی صفحه است
  const [resultOpen, setResultOpen] = useState(false);
  const [grouped, setGrouped] = useState(false);          // جدول ساده؛ سرگروه‌ها فقط با انتخاب کاربر
  const [preview, setPreview] = useState(null);           // {url, title} پیش‌نمایش چاپ

  const showToast = m => { setToast(m); setTimeout(() => setToast(''), 3000); };

  /* ── بارگذاری اولیه یا تغییر سال: بازه‌ها از نو، انتخاب‌ها پاک ── */
  const loadYear = useCallback(async y => {
    const j = await API(`/init/?year=${encodeURIComponent(y || '')}`);
    if (!j.ok) { setError(j.msg); return null; }
    setBoot(b => b || j);
    setYear(String(j.year)); yearRef.current = String(j.year);
    setFromDate(j.from_date); setToDate(j.to_date);
    setFromNo(String(j.from_no || '')); setToNo(String(j.to_no || ''));
    const marks = {};
    j.financial_years.forEach(it => { marks[String(it.id)] = Number(it.id) === Number(j.year) ? 'sel' : 'unsel'; });
    setFilters({ FinancialYear: { items: j.financial_years, marks, loading: false } });
    setResult(null); setDrill(null);
    return j;
  }, []);

  useEffect(() => {
    loadYear(initYear).then(j => { if (j) setReport(j.default_report); });
  }, []); // eslint-disable-line

  const commitYear = () => {
    const y = toEng(year).trim();
    if (y && y !== yearRef.current) loadYear(y);
  };

  /* ── فهرست موارد یک گزینه ── */
  const loadItems = useCallback(async (key, opt = {}) => {
    setFilters(f => ({ ...f, [key]: { items: (f[key] && f[key].items) || [], marks: (f[key] && f[key].marks) || {}, loading: true } }));
    const j = await API(`/items/${key}/?year=${yearRef.current}${opt.all ? '&all=1' : ''}`);
    const items = j.ok ? j.data : [];
    setFilters(f => {
      const marks = { ...((!opt.reset && f[key] && f[key].marks) || {}) };
      (opt.unselected || []).forEach(id => { marks[String(id)] = 'unsel'; });
      return { ...f, [key]: { items, marks, loading: false } };
    });
    if (!j.ok) setError(j.msg);
    return items;
  }, []);

  const openFilter = dim => {
    setPicking(dim);
    if (!filters[dim.key] || !filters[dim.key].items.length) loadItems(dim.key, { all: dim.key === 'AccountingSubCodingCode' && allSub });
  };

  const setRole = (key, role) => setRoles(r => {
    const n = { ...r };
    if (role) n[key] = role; else delete n[key];
    return n;
  });

  /* ── state ارسالی به سرور ── */
  const buildState = () => {
    const f = {};
    Object.entries(filters).forEach(([k, v]) => {
      if (!v.items || !v.items.length) return;
      const out = { sel: [], unsel: [], desel: [] };
      v.items.forEach(it => {
        const m = v.marks[String(it.id)] || 'sel';
        out[m].push(it.v);
      });
      if (out.unsel.length || out.desel.length) f[k] = out;
    });
    return {
      year: Number(toEng(year)) || 0, report, scale, value,
      from_date: fromDate, to_date: toDate,
      from_no: Number(toEng(fromNo)) || 0, to_no: Number(toEng(toNo)) || 0,
      interval, kinds, document_detail: docDetail, dims: roles, filters: f,
    };
  };

  const run = async (stateOverride) => {
    const st = stateOverride || buildState();
    if (st.report !== GUARANTEES && !Object.keys(st.dims).length) {
      setError('حداقل یک گزینه را در «شرایط گزارش» به‌عنوان سطر یا سرگروه انتخاب کنید');
      return;
    }
    setRunning(true); setError(''); setDrill(null); setSelected(null);
    const j = await API('/run/', { method: 'POST', body: JSON.stringify({ state: st }) });
    setRunning(false);
    if (!j.ok) { setError(j.msg); return; }
    setResult(j); setLimit(PAGE_SIZE); setLastState(st);
    setResultOpen(true);
  };

  /* ── ریز اقلام ── */
  const openDrill = async (k, column) => {
    if (!result || !lastState || lastState.report >= GUARANTEES) return;
    const row = {};
    result.columns.forEach((c, i) => { if (!(c.name in row)) row[c.name] = result.rows[k][i]; });
    setDrill({ column, rows: [], loading: true });
    const j = await API('/drill/', { method: 'POST', body: JSON.stringify({ state: lastState, row, column }) });
    if (!j.ok) { setDrill(null); setError(j.msg); return; }
    setDrill({ column, rows: j.data, loading: false });
  };

  /* ── چاپ ── */
  const doPrint = async () => {
    if (!lastState) return;
    setPrinting(true); setError('');
    try {
      const res = await fetch('/api/accounting/general-report/print/', {
        method: 'POST', headers: { 'Content-Type': 'application/json', ...auth() },
        body: JSON.stringify({ state: lastState, opts: printOpts }),
      });
      if (!res.ok) { const j = await jsonOf(res); setError(j.msg); return; }
      const url = URL.createObjectURL(await res.blob());
      setPreview({ url, title: printOpts.title.replace(/\$/g, ' ').trim() || `گزارش کلی حسابداری — ${boot.report_types[lastState.report]}` });
      setPrintOpen(false);
    } catch (e) {
      setError('ارتباط با سرور برقرار نشد');
    } finally {
      setPrinting(false);
    }
  };

  const doExcel = () => {
    if (!result) return;
    const vis = result.columns.map((c, i) => ({ ...c, i })).filter(c => c.visible);
    exportGrid({
      title: printOpts.title || `گزارش کلی حسابداری - ${boot.report_types[lastState.report]}`,
      subtitle: `از تاریخ ${lastState.from_date} تا ${lastState.to_date} — مبالغ به ${boot.scales[lastState.scale]}`,
      columns: vis.map(c => ({ key: `c${c.i}`, label: c.name, num: c.num })),
      rows: result.rows.map(r => vis.reduce((o, c) => { o[`c${c.i}`] = r[c.i]; return o; }, {})),
    });
  };

  const doSamad = async () => {
    if (!lastState) return;
    const res = await fetch('/api/accounting/general-report/samad/', {
      method: 'POST', headers: { 'Content-Type': 'application/json', ...auth() },
      body: JSON.stringify({ state: lastState }),
    }).catch(() => null);
    if (!res) { setError('ارتباط با سرور برقرار نشد'); return; }
    if (!res.ok) { const j = await jsonOf(res); setError(j.msg); return; }
    const cd = res.headers.get('Content-Disposition') || '';
    const m = /filename\*=UTF-8''([^;]+)/.exec(cd);
    const a = document.createElement('a');
    a.href = URL.createObjectURL(await res.blob());
    a.download = m ? decodeURIComponent(m[1]) : 'samad.xlsx';
    document.body.appendChild(a); a.click(); a.remove();
    setTimeout(() => URL.revokeObjectURL(a.href), 60000);
    showToast('فایل سماد ساخته شد');
  };

  /* ── گزارش‌های ذخیره‌شده ── */
  const openSaved = async () => {
    setSavedOpen(true); setSavedLoading(true);
    const j = await API('/saved/');
    setSavedLoading(false);
    if (j.ok) setSavedList(j.data); else setError(j.msg);
  };

  const loadSaved = async id => {
    const j = await API(`/saved/${id}/`);
    if (!j.ok) { setError(j.msg); return; }
    const d = j.data;
    setSavedOpen(false);
    setCurrent({ id: d.id, title: d.title, remark: d.remark });
    setPrintOpts(o => ({ ...o, title: d.title }));
    setReport(d.report);
    setRoles(d.dims);
    // همهٔ انتخاب‌های قبلی «انتخاب»، سپس موارد ذخیره‌شده «انتخاب‌نشده»
    const keys = Object.keys(d.unselected || {});
    setFilters(f => {
      const n = {};
      Object.entries(f).forEach(([k, v]) => { n[k] = { ...v, marks: {} }; });
      return n;
    });
    for (const k of keys) {
      // eslint-disable-next-line no-await-in-loop
      await loadItems(k, { reset: true, unselected: d.unselected[k] });
    }
    // ساخت state با همان انتخاب‌ها و اجرای خودکار
    setTimeout(() => setAutoRun(x => x + 1), 0);
  };
  const [autoRun, setAutoRun] = useState(0);
  useEffect(() => { if (autoRun) run(); }, [autoRun]); // eslint-disable-line

  const saveReport = async (id, title, remark) => {
    if (!String(title || '').trim()) { setError('عنوان گزارش مشخص نشده است'); return; }
    const unselected = {};
    Object.entries(filters).forEach(([k, v]) => {
      const ids = (v.items || []).filter(it => (v.marks[String(it.id)] || 'sel') !== 'sel').map(it => it.id);
      if (ids.length) unselected[k] = ids;
    });
    const body = JSON.stringify({ title, remark, report, dims: roles, unselected });
    const j = id
      ? await API(`/saved/${id}/`, { method: 'PUT', body })
      : await API('/saved/', { method: 'POST', body });
    if (!j.ok) { setError(j.msg); return; }
    setCurrent({ id: j.id, title, remark });
    setPrintOpts(o => ({ ...o, title }));
    showToast(j.msg);
    const l = await API('/saved/');
    if (l.ok) setSavedList(l.data);
  };

  const deleteReport = async id => {
    const j = await API(`/saved/${id}/`, { method: 'DELETE' });
    if (!j.ok) { setError(j.msg); return; }
    if (current && current.id === id) setCurrent(null);
    showToast(j.msg);
    setSavedList(l => l.filter(r => r.id !== id));
  };

  /* ── ابعاد ── */
  const dims = useMemo(() => (boot && boot.dims) || [], [boot]);
  const topDims = (boot ? boot.top : []).map(k => dims.find(d => d.key === k)).filter(Boolean);
  const otherDims = dims.filter(d => !d.top && (!dimQ || d.title.includes(dimQ)));
  const isPivot = report === PIVOT;
  const isGuarantee = report === GUARANTEES;

  const summary = useMemo(() => {
    const by = { row: [], group: [], sub: [] };
    dims.forEach(d => { if (roles[d.key]) by[roles[d.key]].push(d.title); });
    const fl = dims.filter(d => filterLabel(filters[d.key]).active).map(d => `${d.title} (${filterLabel(filters[d.key]).text})`);
    return { ...by, fl };
  }, [dims, roles, filters]);

  const clearAll = () => {
    setRoles({});
    setFilters(f => {
      const n = {};
      Object.entries(f).forEach(([k, v]) => { n[k] = { ...v, marks: k === 'FinancialYear' ? v.marks : {} }; });
      return n;
    });
  };

  const closePreview = () => { if (preview) URL.revokeObjectURL(preview.url); setPreview(null); };

  const toolbar = (
    <div style={s.tbWrap}>
      {/* ردیف ۱: نوع گزارش و بازه‌ها */}
      <div style={s.tbRow}>
        <div style={s.field}>
          <label style={ui.flabel}>نوع گزارش</label>
          <select value={report} onChange={e => setReport(Number(e.target.value))} style={{ ...s.select, minWidth: '250px' }}>
            {(boot ? boot.report_types : []).map((t, i) => <option key={i} value={i}>{t}</option>)}
          </select>
        </div>
        {isPivot && (
          <div style={s.field}>
            <label style={ui.flabel}>مبلغ</label>
            <select value={value} onChange={e => setValue(Number(e.target.value))} style={s.select}>
              {(boot ? boot.values : []).map((t, i) => <option key={i} value={i}>{t}</option>)}
            </select>
          </div>
        )}
        <div style={s.field}>
          <label style={ui.flabel}>سال مالی</label>
          <input value={toFa(year)} maxLength={4} style={ui.yearInput}
            onChange={e => setYear(toEng(e.target.value).replace(/\D/g, ''))}
            onBlur={commitYear} onKeyDown={e => { if (e.key === 'Enter') commitYear(); }} />
        </div>
        <div style={s.field}>
          <label style={ui.flabel}>از تاریخ</label>
          <div style={{ width: '150px' }}><JalaliDatePicker value={fromDate} onChange={setFromDate} /></div>
        </div>
        <div style={s.field}>
          <label style={ui.flabel}>تا تاریخ</label>
          <div style={{ width: '150px' }}><JalaliDatePicker value={toDate} onChange={setToDate} /></div>
        </div>
        <div style={s.field}>
          <label style={ui.flabel}>سند از</label>
          <input value={toFa(fromNo)} onChange={e => setFromNo(toEng(e.target.value).replace(/\D/g, ''))} style={s.numIn} />
        </div>
        <div style={s.field}>
          <label style={ui.flabel}>تا</label>
          <input value={toFa(toNo)} onChange={e => setToNo(toEng(e.target.value).replace(/\D/g, ''))} style={s.numIn} />
        </div>
        <div style={s.field}>
          <label style={ui.flabel}>مبالغ به</label>
          <select value={scale} onChange={e => setScale(Number(e.target.value))} style={s.select}>
            {(boot ? boot.scales : []).map((t, i) => <option key={i} value={i}>{t}</option>)}
          </select>
        </div>
        <div style={{ ...ui.toolBtns, marginRight: 'auto' }}>
          <button onClick={() => run()} disabled={running || !boot} style={s.btnRun}>
            {running ? '⏳ در حال آماده‌سازی…' : '▶ آماده کردن گزارش'}
          </button>
          {result && <button onClick={() => setResultOpen(true)} style={s.btnShow}>📋 نمایش آخرین نتیجه</button>}
          <button onClick={openSaved} style={s.btnSaved}>📁 گزارشات ذخیره شده</button>
        </div>
      </div>
      {/* ردیف ۲: تیک‌ها */}
      <div style={{ ...s.tbRow, gap: '6px' }}>
        <label style={{ ...s.chip, ...(interval ? s.chipOn : {}) }}>
          <input type="checkbox" checked={interval} onChange={e => setOnlyRange(e.target.checked)} />فقط محدوده تعیین شده
        </label>
        <span style={s.sep} />
        {[['normal', 'اسناد عادی'], ['opening', 'اسناد افتتاحیه'], ['finishing', 'اسناد اختتامیه']].map(([k, t]) => (
          <label key={k} style={{ ...s.chip, ...(kinds[k] ? s.chipOn : {}) }}>
            <input type="checkbox" checked={kinds[k]} onChange={e => setKinds(o => ({ ...o, [k]: e.target.checked }))} />{t}
          </label>
        ))}
        {roles.Document && (
          <>
            <span style={s.sep} />
            <label style={{ ...s.chip, ...(docDetail ? s.chipOn : {}) }}>
              <input type="checkbox" checked={docDetail} onChange={e => setDocDetail(e.target.checked)} />اقلام سند
            </label>
          </>
        )}
        {current && <span style={s.currentBadge}>گزارش: {current.title}</span>}
      </div>
    </div>
  );

  return (
    <>
      <ListPage user={user} onBack={onBack} title="گزارش کلی حسابداری" subtitle="تنظیم حساب — سامانه ارجان"
        toolbar={toolbar} error={resultOpen ? '' : error}>
        <div style={s.panel}>
          <div style={s.panelHead}>
            <span style={{ fontWeight: 800, color: '#1a2f50', whiteSpace: 'nowrap' }}>شرایط گزارش</span>
            <div style={s.summary}>
              {summary.row.length > 0 && <span style={{ ...s.sumChip, borderColor: ROLE_COLORS.row, color: ROLE_COLORS.row }}>سطر: {summary.row.join('، ')}</span>}
              {summary.group.length > 0 && <span style={{ ...s.sumChip, borderColor: ROLE_COLORS.group, color: ROLE_COLORS.group }}>{isPivot ? 'ستون' : 'سرگروه'}: {summary.group.join('، ')}</span>}
              {summary.sub.length > 0 && <span style={{ ...s.sumChip, borderColor: ROLE_COLORS.sub, color: ROLE_COLORS.sub }}>{isPivot ? 'ستون' : 'زیرگروه'}: {summary.sub.join('، ')}</span>}
              {summary.fl.length > 0 && <span style={{ ...s.sumChip, borderColor: '#ef6c00', color: '#ef6c00' }}>فیلتر: {summary.fl.join('، ')}</span>}
              {!summary.row.length && !summary.group.length && !summary.sub.length && !summary.fl.length &&
                <span style={{ color: '#999', fontSize: '12px' }}>هنوز گزینه‌ای انتخاب نشده است</span>}
            </div>
            <button style={s.smallBtn} onClick={clearAll}>پاک کردن انتخاب‌ها</button>
          </div>
          <div style={s.panelBody}>
            {isGuarantee && <div style={s.hint}>در «وضعیت تضمینات قراردادها» فقط فیلترهای قرارداد، پیمانکار، منبع اعتبار و سال به کار می‌رود.</div>}
            {isPivot && <div style={s.hint}>در «گزارش انتخابی»، گزینه‌های «سطر» سطرهای گزارش و گزینه‌های «ستون» ستون‌های آن هستند؛ مقدار هر خانه از کشوی «مبلغ» است.</div>}
            {!isPivot && !isGuarantee && (
              <div style={s.legend}>
                <span><b style={{ color: ROLE_COLORS.row }}>سطر</b>: هر مقدار، ستونی در هر ردیف جدول</span>
                <span><b style={{ color: ROLE_COLORS.group }}>سرگروه</b>: ردیف‌ها به ترتیب آن مرتب و در چاپ زیر عنوانش گروه می‌شوند</span>
                <span><b style={{ color: ROLE_COLORS.sub }}>زیرگروه</b>: گروه میانی با جمع، داخل سرگروه</span>
                <span style={{ color: '#888' }}>هر گزینه فقط یک نقش می‌گیرد</span>
              </div>
            )}
            <div style={s.secTitle}>گزینه‌های پرکاربرد</div>
            <div style={s.dimGrid}>
              {topDims.map(d => (
                <DimRow key={d.key} dim={d} role={roles[d.key]} filter={filters[d.key]} pivot={isPivot}
                  onRole={setRole} onFilter={openFilter} />
              ))}
            </div>
            <div style={{ display: 'flex', alignItems: 'center', gap: '10px', margin: '12px 0 6px' }}>
              <button style={s.moreBtn} onClick={() => setShowMore(v => !v)}>
                {showMore ? '▴ بستن سایر گزینه‌ها' : `▾ سایر گزینه‌ها (${toFa(dims.length - topDims.length)})`}
              </button>
              {showMore && <input style={{ ...ui.input, width: '220px', padding: '6px 10px' }} value={dimQ}
                onChange={e => setDimQ(e.target.value)} placeholder="جستجوی گزینه…" />}
            </div>
            {showMore && (boot ? boot.categories : []).map(cat => {
              const list = otherDims.filter(d => d.cat === cat.key);
              if (!list.length) return null;
              return (
                <div key={cat.key}>
                  <div style={s.secTitle}>{cat.title}</div>
                  <div style={s.dimGrid}>
                    {list.map(d => (
                      <DimRow key={d.key} dim={d} role={roles[d.key]} filter={filters[d.key]} pivot={isPivot}
                        onRole={setRole} onFilter={openFilter} />
                    ))}
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </ListPage>

      {running && (
        <div style={{ ...ui.overlay, zIndex: 1100 }}>
          <div style={s.waitBox}>⏳ در حال آماده کردن گزارش، لطفا منتظر بمانید…</div>
        </div>
      )}
      {resultOpen && result && lastState && boot && (
        <ResultModal boot={boot} st={lastState} result={result} limit={limit} onMore={p => setLimit(p * PAGE_SIZE)}
          grouped={grouped} setGrouped={setGrouped} selected={selected} onSelect={setSelected}
          drill={drill} onDrill={openDrill} onCloseDrill={() => setDrill(null)}
          onOpenDoc={r => setDocOpen({
            doc: { id: r.DocumentId, document_no: r['سند'], document_date: r['تاریخ'],
                   document_remark: r['شرح سند'], is_closed: !!r.IsClosed },
            year: r.DocYear,
          })}
          error={error} onPrint={() => setPrintOpen(true)} onExcel={doExcel} onSamad={doSamad}
          onClose={() => { setResultOpen(false); setDrill(null); setError(''); }} />
      )}
      {picking && (
        <FilterModal dim={picking} filter={filters[picking.key]} loading={!!(filters[picking.key] && filters[picking.key].loading)}
          allSub={allSub}
          onAllSub={v => { setAllSub(v); loadItems('AccountingSubCodingCode', { all: v }); }}
          onChange={marks => setFilters(f => ({ ...f, [picking.key]: { ...f[picking.key], marks } }))}
          onClose={() => setPicking(null)} />
      )}
      {savedOpen && (
        <SavedModal list={savedList} loading={savedLoading} current={current}
          onOpen={loadSaved} onSave={saveReport} onDelete={deleteReport} onClose={() => setSavedOpen(false)} />
      )}
      {printOpen && (
        <PrintModal report={lastState ? lastState.report : report} opts={printOpts} setOpts={setPrintOpts}
          busy={printing} onPrint={doPrint} onClose={() => setPrintOpen(false)} />
      )}
      {preview && <PrintPreview url={preview.url} title={preview.title} onClose={closePreview} />}
      {docOpen && <DocumentDetailModal doc={docOpen.doc} year={docOpen.year} onClose={() => setDocOpen(null)} />}
      {toast && <div style={ui.toast}>{toast}</div>}
    </>
  );
}

const s = {
  warnLine: { background: '#fff3e0', color: '#c62828', border: '1px solid #ffcc80', borderRadius: '8px',
              padding: '7px 12px', fontSize: '12.5px', fontWeight: 600 },
  bigModal: { ...ui.modal, maxWidth: 'none', width: '100%', height: '100%', maxHeight: 'none', overflow: 'hidden' },
  mSub:     { fontSize: '12px', color: 'rgba(255,255,255,0.72)' },
  mTools:   { display: 'flex', alignItems: 'center', gap: '8px', flexWrap: 'wrap', justifyContent: 'flex-end' },
  mFoot:    { display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '10px', padding: '8px 16px',
              borderTop: '1px solid #e8ecf1', background: '#f8f9fb', borderRadius: '0 0 16px 16px', flexShrink: 0 },
  chipDark: { display: 'inline-flex', alignItems: 'center', gap: '6px', padding: '6px 12px', borderRadius: '20px',
              border: '1px solid rgba(255,255,255,0.3)', color: '#e3eaf3', fontSize: '12px', cursor: 'pointer', userSelect: 'none' },
  chipDarkOn: { background: 'rgba(200,168,75,0.25)', borderColor: '#C8A84B', color: '#fff', fontWeight: 700 },
  btnShow:  { padding: '9px 16px', background: 'linear-gradient(135deg,#37474f,#546e7a)', color: '#fff', border: 'none', borderRadius: '8px', cursor: 'pointer', fontFamily: 'inherit', fontSize: '13px', fontWeight: 700 },
  waitBox:  { background: '#fff', borderRadius: '14px', padding: '22px 34px', fontSize: '14px', fontWeight: 700, color: '#1a2f50', boxShadow: '0 20px 60px rgba(0,0,0,0.4)' },
  legend:   { display: 'flex', flexWrap: 'wrap', gap: '6px 18px', fontSize: '12px', color: '#455a64', background: '#f7f9fc',
              border: '1px solid #e6eaf0', borderRadius: '8px', padding: '7px 12px', marginBottom: '6px' },
  tbWrap:   { display: 'flex', flexDirection: 'column', gap: '10px', width: '100%' },
  tbRow:    { display: 'flex', alignItems: 'flex-end', gap: '10px', flexWrap: 'wrap' },
  field:    { display: 'flex', flexDirection: 'column', gap: '3px' },
  select:   { padding: '7px 10px', border: '1.5px solid #d5dbe5', borderRadius: '8px', fontSize: '13px', fontFamily: 'inherit', background: '#fff', outline: 'none', color: '#1a2f50', fontWeight: 600 },
  numIn:    { width: '70px', padding: '7px 8px', border: '1.5px solid #d5dbe5', borderRadius: '8px', fontSize: '13px', fontFamily: 'inherit', textAlign: 'center', outline: 'none' },
  chip:     { display: 'inline-flex', alignItems: 'center', gap: '6px', padding: '5px 11px', border: '1px solid #dde3ec', borderRadius: '20px', fontSize: '12px', color: '#555', background: '#fafbfd', cursor: 'pointer', userSelect: 'none' },
  chipOn:   { background: '#e8f0fe', borderColor: '#90b4f0', color: '#1a3d7c', fontWeight: 600 },
  sep:      { width: '1px', height: '20px', background: '#e0e4ea', margin: '0 4px' },
  currentBadge: { padding: '4px 12px', borderRadius: '20px', background: '#fff8e1', border: '1px solid #ffe082', color: '#6d4c00', fontSize: '12px', fontWeight: 600 },
  btnRun:   { padding: '9px 20px', background: 'linear-gradient(135deg,#1b5e20,#2e7d32)', color: '#fff', border: 'none', borderRadius: '8px', cursor: 'pointer', fontFamily: 'inherit', fontSize: '13px', fontWeight: 700 },
  btnPrint: { padding: '9px 16px', background: 'linear-gradient(135deg,#0d47a1,#1565c0)', color: '#fff', border: 'none', borderRadius: '8px', cursor: 'pointer', fontFamily: 'inherit', fontSize: '13px', fontWeight: 700 },
  btnExcel: { padding: '9px 16px', background: 'linear-gradient(135deg,#1b5e20,#388e3c)', color: '#fff', border: 'none', borderRadius: '8px', cursor: 'pointer', fontFamily: 'inherit', fontSize: '13px', fontWeight: 700 },
  btnSamad: { padding: '9px 16px', background: 'linear-gradient(135deg,#4e342e,#6d4c41)', color: '#fff', border: 'none', borderRadius: '8px', cursor: 'pointer', fontFamily: 'inherit', fontSize: '13px', fontWeight: 700 },
  btnSaved: { padding: '9px 16px', background: 'linear-gradient(135deg,#8d6e00,#C8A84B)', color: '#fff', border: 'none', borderRadius: '8px', cursor: 'pointer', fontFamily: 'inherit', fontSize: '13px', fontWeight: 700 },
  panel:    { background: '#fff', borderRadius: '12px', height: '100%', minHeight: 0, display: 'flex', flexDirection: 'column' },
  panelHead: { display: 'flex', alignItems: 'center', gap: '12px', padding: '10px 16px', borderBottom: '1px solid #eef1f5', flexShrink: 0 },
  panelBody: { padding: '10px 16px 14px', overflowY: 'auto', minHeight: 0, flex: 1 },
  summary:  { display: 'flex', flexWrap: 'wrap', gap: '6px', flex: 1, minWidth: 0 },
  sumChip:  { padding: '2px 10px', border: '1px solid', borderRadius: '14px', fontSize: '11.5px', fontWeight: 600, background: '#fff', maxWidth: '100%', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' },
  hint:     { background: '#f1f6ff', border: '1px solid #d6e4ff', color: '#24476f', borderRadius: '8px', padding: '7px 12px', fontSize: '12px', marginBottom: '8px' },
  secTitle: { fontSize: '12px', fontWeight: 800, color: '#7a6420', margin: '8px 2px 6px', borderRight: '3px solid #C8A84B', paddingRight: '8px' },
  dimGrid:  { display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(430px, 1fr))', gap: '6px' },
  dimRow:   { display: 'flex', alignItems: 'center', gap: '8px', padding: '5px 8px', border: '1px solid #e6eaf0', borderRadius: '9px', background: '#fff' },
  dimTitle: { flex: 1, minWidth: 0, fontSize: '12.5px', fontWeight: 600, color: '#263238', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', display: 'flex', alignItems: 'center', gap: '6px' },
  dot:      { width: '8px', height: '8px', borderRadius: '50%', flexShrink: 0 },
  seg:      { display: 'inline-flex', border: '1px solid #cfd8e3', borderRadius: '8px', overflow: 'hidden', flexShrink: 0 },
  segBtn:   { padding: '4px 9px', fontSize: '11.5px', fontFamily: 'inherit', border: 'none', borderLeft: '1px solid #e3e8ef', background: '#f7f9fc', color: '#455a64', cursor: 'pointer', minWidth: '44px' },
  filterBtn: { padding: '4px 10px', fontSize: '11.5px', fontFamily: 'inherit', border: '1px dashed #c3ccd8', borderRadius: '8px', background: '#fff', color: '#607d8b', cursor: 'pointer', minWidth: '78px', flexShrink: 0 },
  filterBtnOn: { borderStyle: 'solid', borderColor: '#ef6c00', color: '#e65100', background: '#fff7ef', fontWeight: 700 },
  moreBtn:  { padding: '6px 14px', fontSize: '12.5px', fontFamily: 'inherit', border: '1px solid #c8a84b', borderRadius: '8px', background: '#fffaf0', color: '#7a5d00', cursor: 'pointer', fontWeight: 700 },
  smallBtn: { padding: '5px 12px', fontSize: '12px', fontFamily: 'inherit', border: '1px solid #d5dbe5', borderRadius: '7px', background: '#f5f7fa', color: '#37474f', cursor: 'pointer' },
  chk:      { display: 'flex', alignItems: 'center', gap: '8px', fontSize: '13px', color: '#333', cursor: 'pointer' },
  thSticky: { ...ui.th, position: 'sticky', top: 0, zIndex: 1 },
  placeholder: { height: '100%', minHeight: '180px', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', gap: '8px', color: '#607d8b', fontSize: '13.5px' },
  num:      { direction: 'ltr', textAlign: 'left', fontWeight: 600, fontVariantNumeric: 'tabular-nums' },
  gHead:    { padding: '7px 12px', background: '#dfe7f1', color: '#0d2a4a', fontWeight: 800, fontSize: '12.5px', textAlign: 'right', whiteSpace: 'pre-line', borderTop: '2px solid #9fb3cc' },
  sHead:    { padding: '6px 22px', background: '#eef3f8', color: '#234', fontWeight: 700, fontSize: '12px', textAlign: 'right', whiteSpace: 'pre-line' },
  sTotal:   { padding: '6px 7px', background: '#f3f6f9', fontWeight: 700, fontSize: '12px', borderBottom: '1px solid #e1e6ec', color: '#234' },
  gTotal:   { padding: '7px 7px', background: '#e6ecf3', fontWeight: 800, fontSize: '12px', borderBottom: '2px solid #c9d4e1', color: '#0d2a4a' },
  foot:     { padding: '8px 7px', background: '#0d2137', color: '#C8A84B', fontWeight: 800, fontSize: '12.5px', textAlign: 'center', whiteSpace: 'nowrap' },
  drill:    { display: 'flex', flexDirection: 'column', height: '100%', minHeight: 0 },
  drillHead: { display: 'flex', alignItems: 'center', gap: '12px', padding: '8px 12px', borderBottom: '1px solid #e0e6ec', background: '#f4faf6', flexWrap: 'wrap', flexShrink: 0 },
  sumBox:   { display: 'flex', gap: '14px', fontSize: '12px', color: '#37474f', flexWrap: 'wrap' },
};
